import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import { X } from "lucide-react";
import { useScrollLock } from "../../lib/useScrollLock";

/**
 * Global, reusable responsive modal shell used by EVERY popup on the site
 * (news, team, registration, showreel, and any future one).
 *
 * Guarantees, everywhere and on every screen size:
 *  - never taller than ~88% of the viewport height (max-h-[88dvh])
 *  - perfectly centered, background page scroll locked (useScrollLock)
 *  - the ONLY vertical scroll lives inside <ModalScroll> (page behind never scrolls)
 *  - the close button stays visible/accessible even when scrolling the content
 *  - media never overflows the window (see <ModalMedia>)
 *
 * It changes NO colours, texts, data or section behaviour — only the container's
 * responsive layout so content is always fully reachable.
 */
const SIZES = { sm: "max-w-md", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-3xl", "2xl": "max-w-4xl" };

export const Modal = ({ open, onClose, children, size = "lg", testid = "modal", className = "", rowLayout = false }) => {
  useScrollLock(!!open);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  const maxW = SIZES[size] || SIZES.lg;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[10001] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose} data-testid={testid}>
          <motion.div
            onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
            initial={{ scale: 0.96, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 20 }}
            style={{
              borderColor: "var(--fx-modal-border, rgba(255,255,255,0.1))",
              borderWidth: "var(--fx-modal-border-w, 1px)",
              boxShadow: "var(--fx-modal-glow, none)",
            }}
            className={`relative w-full ${maxW} max-h-[88dvh] flex ${rowLayout ? "flex-col md:flex-row" : "flex-col"} rounded-2xl border bg-[#0a0a0a] overflow-hidden ${className}`}>
            <button
              onClick={onClose} data-testid={`${testid}-close`} aria-label="Fermer"
              className="absolute top-4 right-4 z-30 h-10 w-10 rounded-full bg-black/60 backdrop-blur border border-white/20 text-white/80 flex items-center justify-center transition-colors hover:[border-color:var(--fx-modal-border,#2e6bff)] hover:[color:var(--fx-modal-border,#2e6bff)]">
              <X size={20} />
            </button>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/** The single scrollable region of a modal. Use `min-h-0` so it scrolls inside the flex column. */
export const ModalScroll = ({ children, className = "", testid = "modal-scroll" }) => (
  <div className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${className}`} data-testid={testid} data-lenis-prevent>
    {children}
  </div>
);

/**
 * Responsive media header for a modal. Caps the media height so it never eats the whole
 * window, and shows the full media without cropping (pass fit="contain" to MediaView / imgs).
 */
export const ModalMedia = ({ children, className = "" }) => (
  <div className={`w-full shrink-0 bg-[#0e0e0e] h-[34dvh] sm:h-[38dvh] md:h-[42dvh] ${className}`}>
    {children}
  </div>
);
